import { faFileArrowUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DragEvent,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

const ACCEPTED_MIME_PREFIXES = ['image/'];
const ACCEPTED_MIME_TYPES = ['application/pdf'];

const Wrapper = styled.div<{ $isDragging: boolean; $padded: boolean }>`
  position: relative;
  border: 2px dashed transparent;
  border-radius: ${({ theme }) => theme.sizes.radius.input}px;
  ${({ $padded }) =>
    $padded &&
    css`
      padding: 6px;
    `}
  transition: border-color 0.1s;
  ${({ $isDragging, theme }) =>
    $isDragging &&
    css`
      border-color: ${theme.colors.background.button};
    `}
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: ${({ theme }) => theme.colors.background.primary};
  opacity: 0.85;
  color: ${({ theme }) => theme.colors.background.button};
  border-radius: ${({ theme }) => theme.sizes.radius.input}px;
  font-weight: 600;
  pointer-events: none;
  z-index: 10;
`;

interface Props {
  onFilesDropped(files: File[]): void;
  /** Text shown in the overlay when dragging over the zone */
  overlayLabel?: string;
  /** When true, the zone ignores all drag/drop events */
  disabled?: boolean;
  /** Adds inner padding so the dashed border has breathing room around content */
  padded?: boolean;
  className?: string;
  children: ReactNode;
}

const isAcceptedFile = (file: File) =>
  ACCEPTED_MIME_PREFIXES.some((p) => file.type.startsWith(p)) ||
  ACCEPTED_MIME_TYPES.includes(file.type);

const FileDropZone: FC<Props> = ({
  onFilesDropped,
  overlayLabel = 'Släpp för att ladda upp',
  disabled = false,
  padded = false,
  className,
  children,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  // Counter handles nested dragenter/leave events firing on child elements
  const dragCounter = useRef(0);

  useEffect(() => {
    if (disabled) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  }, [disabled]);

  // Reset on drop/dragend anywhere in the window so the overlay clears even
  // if the drop happened outside our element (e.g. user released over the
  // browser chrome or pressed Esc).
  useEffect(() => {
    const reset = () => {
      dragCounter.current = 0;
      setIsDragging(false);
    };
    window.addEventListener('drop', reset);
    window.addEventListener('dragend', reset);
    return () => {
      window.removeEventListener('drop', reset);
      window.removeEventListener('dragend', reset);
    };
  }, []);

  const hasFiles = useCallback((eve: DragEvent) => {
    return Array.from(eve.dataTransfer?.types ?? []).includes('Files');
  }, []);

  // Stop drag events from bubbling to an outer FileDropZone so that nested
  // zones (e.g. a Modal rendered inline inside an assignment row) don't both
  // light up and queue the same file.
  const handleDragEnter = useCallback(
    (eve: DragEvent<HTMLDivElement>) => {
      if (disabled || !hasFiles(eve)) return;
      eve.preventDefault();
      eve.stopPropagation();
      dragCounter.current += 1;
      setIsDragging(true);
    },
    [disabled, hasFiles]
  );

  const handleDragOver = useCallback(
    (eve: DragEvent<HTMLDivElement>) => {
      if (disabled || !hasFiles(eve)) return;
      // preventDefault is required for the drop event to fire
      eve.preventDefault();
      eve.stopPropagation();
    },
    [disabled, hasFiles]
  );

  const handleDragLeave = useCallback(
    (eve: DragEvent<HTMLDivElement>) => {
      if (disabled || !hasFiles(eve)) return;
      eve.stopPropagation();
      dragCounter.current -= 1;
      if (dragCounter.current <= 0) {
        dragCounter.current = 0;
        setIsDragging(false);
      }
    },
    [disabled, hasFiles]
  );

  const handleDrop = useCallback(
    (eve: DragEvent<HTMLDivElement>) => {
      if (disabled || !hasFiles(eve)) return;
      eve.preventDefault();
      eve.stopPropagation();
      dragCounter.current = 0;
      setIsDragging(false);

      const files = Array.from(eve.dataTransfer.files).filter(isAcceptedFile);
      if (files.length > 0) {
        onFilesDropped(files);
      }
    },
    [disabled, hasFiles, onFilesDropped]
  );

  return (
    <Wrapper
      className={className}
      $isDragging={isDragging}
      $padded={padded}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}
      {isDragging && (
        <Overlay>
          <FontAwesomeIcon icon={faFileArrowUp} size="2x" />
          <span>{overlayLabel}</span>
        </Overlay>
      )}
    </Wrapper>
  );
};

export default FileDropZone;
